/**
 * SendMediaChatMessageUseCase
 *
 * Upload-then-write hop for a single media (image/video) chat message. This is
 * the chat branch of Flutter's `MediasRepository.uploadMedia`
 * (lib/data/repository/medias_repository.dart:216-255).
 *
 * Ordering mirrors Flutter exactly (medias_repository.dart:216-255):
 *   1. Generate the message document id (`Uuid().v1()` -> `generateUuidV1()`).
 *   2. `await` the storage upload via `ChatService.uploadChatMedia` — this also
 *      fires the (un-awaited) transcode Cloud Function. Capture the returned
 *      `uploaded` bool.
 *   3. Build the `ChatMessage` entity (`content=""`, `isUploading=true`,
 *      `seenByPeer=false`, type from `mediaType`).
 *   4. `await` the message write via `ChatService.saveChatMessage`. This runs
 *      REGARDLESS of the `uploaded` bool — Flutter writes the message at
 *      `medias_repository.dart:253` unconditionally after the upload at `:220`,
 *      so the chat screen can render the "processing" placeholder even when the
 *      upload failed. `encryptedContent` is the empty string `''` (media
 *      messages carry no text — `content=""` in Flutter,
 *      `medias_repository.dart:244`). `''` is non-null, so `includeIfNull: false`
 *      does not drop it: the message doc gets `content: ""` and the metadata
 *      node gets `last_content: ""` (`chat_data_source.dart:35`), matching
 *      Flutter and `shareWithFriendsUseCase`.
 *   5. Return the `uploaded` bool unchanged (medias_repository.dart:255).
 *
 * `chatId` is recomputed via `ChatUtils.getGroupChatId(currentFirebaseId, peerId)`
 * (medias_repository.dart:239-242) rather than read from `chatUsersInfo.groupChatId`,
 * matching Flutter's recompute.
 *
 * Pure-domain contract: the use case does NOT touch `firestoreService` /
 * `cloudStorageService` / `cloudFunctionsService` directly (the service
 * orchestrates those) and does NOT dispatch to Redux (a presentation concern).
 */

import { ChatService } from '@/domain/services/chat/chatService';
import { ChatUtils } from '@/domain/utils/chatUtils';
import { generateUuidV1 } from '@/domain/utils/uuidUtils';
import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import type { ChatUsersInfoEntity } from '@/domain/entities/info/chatUsersInfoEntity';
import { ChatMessageType } from '@/domain/enums/chatMessageType';
import { appLogger } from '@/domain/services/analytics/appLogger';

export interface SendMediaChatMessageArgs {
  file: File;
  /** `0` = image, otherwise video. */
  mediaType: number;
  aspectRatio?: number;
  durationSeconds: number;
  chatUsersInfo: ChatUsersInfoEntity;
  /**
   * Optional caller-supplied message document id. When the chat list inserts an
   * optimistic "Processing" tile up front (`useChat.sendMedia`), it generates
   * the id, renders the optimistic entry with it, and threads it here so the
   * upload, the transcode CF target, and the persisted `ChatMessage` doc all
   * share the SAME id. That shared id lets the live-listener echo
   * (`handleAdded` documentId-dedupe) REPLACE the optimistic tile instead of
   * appending a duplicate. When omitted (any non-optimistic caller), the use
   * case generates its own id exactly as before — matching Flutter's
   * `Uuid().v1()` at `medias_repository.dart:218`.
   */
  chatDocumentId?: string;
  /**
   * The conversation peer's public JWK, forwarded to `uploadChatMedia` so the
   * encrypt step defensively (re-)derives the RIGHT peer key before
   * `EncryptBinaryUseCase` runs (chat image/thumbnail/video-thumbnail upload as
   * ciphertext). Optional: when `undefined` (the not-yet-updated presentation
   * caller, `useChat.runMediaSendForId`), the upload relies on the
   * chat-load-derived singleton key (`useChat.ts:756`) instead of re-deriving.
   * Threaded down from `useChat`'s `resolvedPeer?.publicJwk` once the
   * presentation layer is updated.
   */
  peerPublicJwk?: JsonWebKey | null;
}

export class SendMediaChatMessageUseCase {
  private chatService: ChatService;

  constructor(chatService: ChatService = ChatService.getInstance()) {
    this.chatService = chatService;
  }

  /**
   * Upload the media (which fires transcoding), then persist the chat message
   * with `isUploading: true`. Returns the upload success boolean unchanged.
   *
   * @returns `true`/`false` from `ChatService.uploadChatMedia` (the message is
   *   written either way — see class JSDoc), or `false` on a thrown failure.
   */
  async execute(args: SendMediaChatMessageArgs): Promise<boolean> {
    const { file, mediaType, aspectRatio, durationSeconds, chatUsersInfo } = args;

    try {
      // (1) Use the caller-supplied document id when the optimistic chat tile
      // already generated one (so the optimistic entry == persisted doc id —
      // see `chatDocumentId` JSDoc); otherwise pre-generate it ourselves,
      // matching Flutter's `Uuid().v1()` at medias_repository.dart:218.
      const chatDocumentId = args.chatDocumentId ?? generateUuidV1();

      // (2) Upload first; this fires the un-awaited transcode CF internally.
      // (medias_repository.dart:220-229). The encrypt step inside the service
      // derives the peer key from `peerPublicJwk` when threaded (else relies on
      // the chat-load-derived singleton key).
      const uploaded = await this.chatService.uploadChatMedia({
        file,
        mediaType,
        aspectRatio,
        durationSeconds,
        chatUsersInfo,
        chatDocumentId,
        peerPublicJwk: args.peerPublicJwk,
      });

      // (3) Build the message entity (medias_repository.dart:234-252).
      // `chatId` is recomputed via getGroupChatId for parity with
      // medias_repository.dart:239-242.
      const chatMessage: ChatMessageEntity = {
        documentId: chatDocumentId,
        idFrom: chatUsersInfo.currentFirebaseId,
        idTo: chatUsersInfo.peerId,
        chatId: ChatUtils.getGroupChatId(chatUsersInfo.currentFirebaseId, chatUsersInfo.peerId),
        timestamp: Date.now(),
        content: '',
        aspectRatio,
        type: mediaType === 0 ? ChatMessageType.image : ChatMessageType.video,
        seenByPeer: false,
        isUploading: true,
        success: true,
        giftInstances: 1,
        users: [chatUsersInfo.currentFirebaseId, chatUsersInfo.peerId],
      };

      // (4) Write the message REGARDLESS of `uploaded` (medias_repository.dart:253
      // runs after :220 unconditionally). `encryptedContent` is the empty string
      // `''` because media messages carry no text body — Flutter writes
      // `content = ""` (medias_repository.dart:244), and `'' ?? x` returns `''`
      // so both `content` and `last_content` land as `""`.
      await this.chatService.saveChatMessage(chatMessage, chatUsersInfo, '');

      // (5) Return the upload bool unchanged (medias_repository.dart:255).
      return uploaded;
    } catch (error) {
      appLogger.error(
        'Failed to send media chat message',
        'SendMediaChatMessageUseCase.execute',
        { error: error as Error },
      );
      return false;
    }
  }
}

// Export singleton instance for convenience (matches the other chat use cases).
export const sendMediaChatMessageUseCase = new SendMediaChatMessageUseCase();
